import { useToast } from '@chakra-ui/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import type { ModelState } from '../dtos'
import { BadRequestError } from '../dtos'
import type { PasswordRules } from '../dtos/Account/PasswordRules'
import { PasswordRuleNames, PasswordRuleValidators } from '../dtos/Account/PasswordRules'
import type { ResetPasswordInfo } from '../dtos/Account/ResetPasswordInfo'
import type { ResetPasswordRequest } from '../dtos/Account/ResetPasswordRequest'
import { yupResolver } from '../rhf'
import apiClient from '../Utils/apiClient'
import { setModelStateErrors } from '../Utils/formModelState'


const baseUrl = import.meta.env.VITE_BASE_URL
const apiUrl = import.meta.env.VITE_ACCOUNT_API
const url = `${baseUrl}/${apiUrl}`

export const useResetPasswordMutation = () =>
    useMutation({
        mutationFn: (variables: ResetPasswordRequest) =>
            apiClient.post(`${url}/ResetPassword`, variables)
    })

export const useGetResetPasswordInfoQuery = (userId?: string, token?: string) =>
    useQuery({
        queryKey: ['resetPasswordInfo', token],
        queryFn: () => apiClient.get<ResetPasswordInfo>(`${url}/GetResetPasswordInfo`, { params: { userId, token } })
            .then(response => response.data),
        enabled: !!userId && !!token
    })

export interface ResetPasswordFormModel extends ResetPasswordRequest {
    userName: string
    passwordConfirm: string
}

export interface PasswordRule {
    key: keyof PasswordRules,
    name: string,
    validator: (v?: string) => boolean
}
export const getPasswordRules = function (rules?: PasswordRules): PasswordRule[] {
    return rules ? Object.keys(rules)
        .filter(key => key && !!(rules as any)[key])
        .map((key) => ({
            key: key as keyof PasswordRules,
            name: PasswordRuleNames.find(x => x[0] === key)![1]((rules as any)[key])!,
            validator: PasswordRuleValidators.find(x => x[0] === key)![1]((rules as any)[key])!,
        })) : []
}

const getResetPasswordSchema = (rules: PasswordRule[]) => {
    let passwordSchema = yup.string()

    for (const rule of rules) {
        passwordSchema = passwordSchema.test(rule.key, rule.name, rule.validator)
    }

    return yup.object().shape({
        newPassword: passwordSchema,
        passwordConfirm: yup.string().oneOf([yup.ref('newPassword'), ''], 'Hesla se neshodují.')
    })
}

export const useGetResetPasswordInfo = (userId?: string, token?: string) => {
    const { data, isError, isLoading } = useGetResetPasswordInfoQuery(userId, token)

    const info = data

    const passwordRules = getPasswordRules(info?.passwordRules)

    return {
        info,
        isError,
        isLoading,
        passwordRules
    }
}

export const useResetPasswordForm = (userId: string, token: string, info: ResetPasswordInfo, onPasswordReset: () => void) => {
    const { mutateAsync: resetPasswordAsync, isSuccess, isError } = useResetPasswordMutation()

    const passwordRules = getPasswordRules(info?.passwordRules)

    const form = useForm<ResetPasswordFormModel>({
        defaultValues: {
            userId,
            token,
            newPassword: '',
            passwordConfirm: '',
            userName: info?.userName
        },
        resolver: yupResolver(getResetPasswordSchema(passwordRules)) as any
    })

    const { handleSubmit, setError, resetField } = form

    useEffect(() => {
        if (info)
            resetField('userName', { defaultValue: info.userName })
    }, [info])

    const toast = useToast()

    const submitHandler = handleSubmit(
        (data) => resetPasswordAsync(data)
            .then(() => onPasswordReset())
            .catch((e: AxiosError) => {
                if (e.response?.status === 400)
                    setModelStateErrors(setError, new BadRequestError(e.response.data as ModelState<ResetPasswordRequest>))
                else
                    toast({
                        title: 'Při resetování hesla došlo k chybě.',
                        description: 'Zkuste to prosím znovu, v případě, že problém přetrvává, kontaktujte podporu.',
                        status: 'error',
                    })
            })
    )

    return {
        info,
        /** true when password was set.  */
        isSuccess,
        /** true when hard error occured and password cannot be reset. */
        isError,

        form,
        submitHandler
    }
}